// ── 매입 감사 로그 모달 ──────────────────────────────────────────────────────
// 카드승인 AuditLogModal 패턴. cashflow_purchase_audit_logs 조회.
// action: upload | rollback | create | update | delete

import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { overlayStyle, modalBtns, btnCancel } from '../revenuemgmt/styles';

const ACTION_LABEL = {
  upload:   { label: '엑셀 업로드', color: '#15803d', bg: '#dcfce7' },
  rollback: { label: '묶음 삭제',   color: '#9a3412', bg: '#fed7aa' },
  create:   { label: '행 추가',      color: '#0369a1', bg: '#dbeafe' },
  update:   { label: '행 수정',      color: '#6d28d9', bg: '#ede9fe' },
  delete:   { label: '행 삭제',      color: '#991b1b', bg: '#fee2e2' },
};

function fmtJson(v) {
  if (v == null) return '';
  try {
    return JSON.stringify(v, null, 2);
  } catch (_) { return String(v); }
}

// 수정 기록의 칸 이름 — 화면 표 머리글과 같은 말 (2026-10-08, 전엔 amount·note 같은 영어 칸 이름이 그대로 보였다)
const FIELD_LABEL = {
  kind: '구분', use_date: '사용일', vendor: '업체', amount: '금액', note: '비고', note2: '비고2',
  tax_invoice_amount: '세금계산서', tax_invoice_issued_date: '발급일', is_excluded: '제외',
  row_color: '색', // 줄 색 표시(2026-11-03)
};
const COLOR_NAME = { yellow: '노랑', green: '초록', blue: '파랑', red: '빨강' };
const MONEY_FIELDS = new Set(['amount', 'tax_invoice_amount']);

function fieldText(k, v) {
  if (k === 'row_color') return COLOR_NAME[v] || '(없음)';
  if (v == null || v === '') return '(빈칸)';
  if (k === 'is_excluded') return v ? '예' : '아니오';
  if (MONEY_FIELDS.has(k)) {
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? `${n.toLocaleString('ko-KR')}원` : String(v);
  }
  return String(v);
}

function summarize(action, oldV, newV) {
  if (action === 'upload') {
    const src = newV?.source === 'paste' ? '붙여넣기' : (newV?.file_name || '-');
    const updated = newV?.updated ? ` · 갱신 ${newV.updated}` : '';
    const skip = newV?.skipped ? ` · skip ${newV.skipped}` : '';
    return `${src} · 신규 ${newV?.inserted || 0}${updated}${skip} · 전체 ${newV?.row_count || 0}`;
  }
  if (action === 'rollback') {
    return `${newV?.file_name ? `${newV.file_name} · ` : ''}줄 ${newV?.deleted_count || 0}건 삭제`;
  }
  if (action === 'create') {
    return `${newV?.use_date || ''} · ${newV?.vendor || ''} · ${Number(newV?.amount || 0).toLocaleString('ko-KR')}원`;
  }
  if (action === 'update') {
    const diffs = [];
    for (const k of Object.keys(FIELD_LABEL)) {
      if (oldV && newV && k in newV && String(oldV[k] ?? '') !== String(newV[k] ?? '')) {
        diffs.push(`${FIELD_LABEL[k]}: ${fieldText(k, oldV[k])} → ${fieldText(k, newV[k])}`);
      }
    }
    return diffs.length ? diffs.join(' · ') : '변경 없음';
  }
  if (action === 'delete') {
    const together = newV?.deleted_together > 1 ? ` (${newV.deleted_together}건 한꺼번에)` : '';
    return `${oldV?.kind ? `${oldV.kind} · ` : ''}${oldV?.use_date || ''} · ${oldV?.vendor || ''} · ${Number(oldV?.amount || 0).toLocaleString('ko-KR')}원${together}`;
  }
  return '';
}

export default function PurchaseAuditLogModal({ companyCode, title, onClose }) {
  useBodyScrollLock(true);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const r = await apiClient.get(`/api/admin/cashflow/purchases/audit-logs?company_code=${companyCode}&limit=200`);
      setRows(r.data?.data || []);
      setLoading(false);
    })();
  }, [companyCode]);

  return (
    <div style={{ ...overlayStyle, alignItems: 'flex-start', paddingTop: 40 }} {...overlayCloseProps}>
      <div
        style={{
          background: 'var(--bg-card)', borderRadius: 10, padding: 24, width: '96vw', maxWidth: 880,
          maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 12 }}>
          {title || '변경 이력'}
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>기록된 변경 이력이 없습니다.</div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {rows.map((r) => {
              const cfg = ACTION_LABEL[r.action] || { label: r.action, color: '#374151', bg: 'var(--bg-subtle)' };
              return (
                <li key={r.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', fontSize: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{
                      fontSize: 11, padding: '1px 8px', borderRadius: 10,
                      background: cfg.bg, color: cfg.color, fontWeight: 700,
                    }}>{cfg.label}</span>
                    <span style={{ color: 'var(--text-sub)' }}>{r.created_at}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>
                      {r.actor_name || `id:${r.actor_id}` || '-'}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text)', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {summarize(r.action, r.old_values, r.new_values)}
                  </div>
                  {(r.action === 'delete' || r.action === 'update') && (
                    <details style={{ marginTop: 4 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)' }}>원본 JSON</summary>
                      <pre style={{
                        fontSize: 11, background: 'var(--bg-subtle)', padding: 8, borderRadius: 4,
                        margin: '4px 0 0', maxHeight: 240, overflow: 'auto', color: 'var(--text)',
                      }}>
old: {fmtJson(r.old_values)}{'\n'}new: {fmtJson(r.new_values)}
                      </pre>
                    </details>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <div style={modalBtns}>
          <button style={btnCancel} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
